import type {
  BulkCloseLeadsBody,
  ChangeLeadStatusBody,
  CreateLeadBody,
  ListLeadsQuery,
  UpdateLeadBody,
} from '@crm/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/api/client';
import { useToast } from '@/components/ui/toast';
import { queryKeys } from '@/lib/query-keys';

export function useLeads(params: ListLeadsQuery) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.leads.list(params),
    queryFn: () => api.leads.list(params),
    // Holds the previous page in place instead of blanking to a skeleton on
    // every page or filter change; the UI dims rather than unmounting.
    placeholderData: keepPreviousData,
  });

  const { limit = 25, offset = 0 } = params;
  const hasNext = offset + limit < (query.data?.total ?? 0);

  useEffect(() => {
    if (!hasNext || query.isPlaceholderData) return;
    const next = { ...params, offset: offset + limit };
    void qc.prefetchQuery({ queryKey: queryKeys.leads.list(next), queryFn: () => api.leads.list(next) });
  }, [qc, params, offset, limit, hasNext, query.isPlaceholderData]);

  return query;
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.leads.detail(id ?? ''),
    queryFn: () => api.leads.get(id as string),
    enabled: Boolean(id),
  });
}

/**
 * §16 creation. It writes a contact, a lead, a note and a follow-up, so the
 * invalidation is deliberately broad — leaving the contact list stale after
 * creating a person through this form is the bug it is written to avoid.
 */
export function useCreateLead() {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeadBody) => api.leads.create(body),
    onSuccess: ({ lead, merged }) => {
      /*
       * Named differently on purpose. A merge lands the operator on a lead
       * with somebody else's title and none of their wording — usually one
       * a FreJun call opened minutes earlier — and with no explanation that
       * reads as the form having saved the wrong thing.
       */
      if (merged) {
        toast.success(
          'Added to an existing lead',
          'This customer already had an open enquiry, so your entry joined it.',
        );
      } else {
        toast.success('Lead created');
      }
      qc.setQueryData(queryKeys.leads.detail(lead.id), lead);
      void qc.invalidateQueries({ queryKey: queryKeys.leads.lists() });
      void qc.invalidateQueries({ queryKey: queryKeys.contacts.all });
      void qc.invalidateQueries({ queryKey: queryKeys.followUps.all });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}

export function useUpdateLead(id: string) {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLeadBody) => api.leads.update(id, body),
    onSuccess: (lead) => {
      toast.success('Lead saved');
      qc.setQueryData(queryKeys.leads.detail(lead.id), lead);
      void qc.invalidateQueries({ queryKey: queryKeys.leads.lists() });
    },
  });
}

/** A status move appends to the timeline, so activities go stale with it. */
export function useChangeLeadStatus(id: string) {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeLeadStatusBody) => api.leads.changeStatus(id, body),
    onSuccess: (lead) => {
      toast.success('Status updated');
      qc.setQueryData(queryKeys.leads.detail(lead.id), lead);
      void qc.invalidateQueries({ queryKey: queryKeys.leads.lists() });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
  });
}

/**
 * Close a batch.
 *
 * Invalidates everything lead-shaped rather than patching the cache: a bulk
 * close changes counts on the dashboard, the needs-attention queue and the
 * Today page at once, and reasoning about which of those to hand-edit is how
 * one of them ends up stale.
 */
export function useBulkCloseLeads() {
  const toast = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkCloseLeadsBody) => api.leads.bulkClose(body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: queryKeys.leads.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      void qc.invalidateQueries({ queryKey: queryKeys.followUps.all });
      void qc.invalidateQueries({ queryKey: queryKeys.activities.all });
      toast.success(
        `${result.closed} closed`,
        result.skipped > 0 ? `${result.skipped} were already closed and were left alone.` : undefined,
      );
    },
  });
}
